import { useEffect, useRef } from 'react';
import gsap from "gsap";
import "swiper/css";
import "swiper/css/navigation";
import 'swiper/css/pagination';

import Banner from "../../component/common/Banner";
import WhySection from "../../component/launchhub/whySection";
import HowWePartner from '../../component/launchhub/howWePartner';
import ChooseYourPath from '../../component/launchhub/choosePath';
import ProgramSection from '../../component/launchhub/ProgramTimeline';
import MediaCarousel from '../../component/common/media';
import CTA from '../../component/common/CTA';
import FAQSection from '../../component/launchhub/faq';
import { Helmet } from 'react-helmet-async';

const BANNER_CONTENT = [{ heading : 'TurningIdeas LaunchHub – Noida' , subHeading : 'A 12-Week Founder Residency With Zero Upfront Fee' , description : 'Build with operators. Execute with experts. Grow with confidence.' , url : 'https://forms.gle/2F4A8ZQeJ4XTmVMa8' , linkText: 'Apply Now' , type : 'launchub'}
]

const mediaData = [
    {
        title: "Student housing Startup Your-space raises Angel Funding",
        date: "January 2025",
        link: "https://www.vccircle.com/exclusive-student-housing-startup-your-space-raises-angel-funding",
    },
    {
        title: "TurningIdeas Venture to help startups with early investments",
        date: "February 2025",
        link: "https://economictimes.indiatimes.com/small-biz/money/turningideas-venture-to-help-startups-with-early-investments/articleshow/52863561.cms",
    },
    {
        title: "42 per cent of Indian Startups Plan to Go Global",
        date: "January 2025",
        link: "https://www.business-standard.com/content/press-releases-ani/42-per-cent-of-indian-startups-plan-to-go-global-in-2022-turningideas-survey-121121601096_1.html",
    },
    {
        title: "Student housing Startup Your-space raises Angel Funding",
        date: "January 2025",
        link: "https://www.vccircle.com/exclusive-student-housing-startup-your-space-raises-angel-funding",
    },
];

const LaunchHub = () => {
    const offerSectionRef = useRef(null);
    const offerHeadingRef = useRef(null);
    const offerCardRefs = useRef([]);
    const offerAnimationTriggered = useRef(false);


    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && !offerAnimationTriggered.current) {
                        offerAnimationTriggered.current = true;
                        animateOfferSection();
                        if (offerSectionRef.current) {
                            observer.unobserve(offerSectionRef.current);
                        }
                    }
                });
            },
            {
                threshold: 0.2,
            }
        );

        const currentRef = offerSectionRef.current; // Capture the ref value

        if (currentRef) {
            observer.observe(currentRef);
        }

        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, []);

    const animateOfferSection = () => {
        const heading = offerHeadingRef.current;
        const cards = offerCardRefs.current;

        const tl = gsap.timeline({ defaults: { duration: 0.8, ease: "power2.out" } });

        tl.fromTo(heading, {
            yPercent: -20,
            opacity: 0,
        }, {
            yPercent: 0,
            opacity: 1,
        });

        tl.fromTo(
            cards,
            {
                yPercent: 20,
                opacity: 0,
            },
            {
                yPercent: 0,
                opacity: 1,
                stagger: 0.15,
            },
            "-=0.5"
        );
    };
    return(
        <div>

            <Helmet>
                <title>TurningIdeas LaunchHub – Noida | 12-Week Execution Residency for Startup Founders</title>

                <meta 
                    name="description" 
                    content="TurningIdeas LaunchHub – Noida is a 12-week execution-first residency for early-stage founders. Get workspace, GTM support, product guidance, investor access, and Demo Day — at zero upfront cost. Apply for the June 2025 batch." 
                />

                {/* 3. Meta Keywords */}
                <meta 
                    name="keywords" 
                    content="startup residency, founder program Noida, TurningIdeas LaunchHub, execution program for startups, GTM support India, startup studio India, Noida startup accelerator, early-stage founders program, entrepreneurship Noida, startup execution support" 
                />

                {/* 4. Open Graph (OG) Tags - Facebook, LinkedIn, WhatsApp */}
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://www.turningideas.com/launchhub" />
                <meta property="og:title" content="TurningIdeas LaunchHub – Noida | 12-Week Execution Residency for Startup Founders" />
                <meta property="og:description" content="A zero-cost, execution-first residency for early-stage founders. Build your startup with TurningIdeas operators: workspace, GTM, product, tech & investor access. Apply for June 2025 Batch." />
                <meta property="og:image" content="https://www.turningideas.com/img/Banner/LaunchHub.png" />
                <meta property="og:site_name" content="TurningIdeas Studio" />

                {/* 5. Twitter Card Tags */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="TurningIdeas LaunchHub – Noida | Execution Residency for Founders" />
                <meta name="twitter:description" content="A 12-week in-office residency for founders to build with GTM, product, tech & investor support — with zero upfront cost. Apply now." />
                <meta name="twitter:image" content="https://www.turningideas.com/img/Banner/LaunchHub.png" />

                <link rel="canonical" href="https://www.turningideas.com/launchhub" />
                </Helmet>

            <Banner content={BANNER_CONTENT} img='img/Banner/LaunchHub.png' page='turningIdeas' />
            
            <WhySection />

            {/* What you get */}
            <section ref={offerSectionRef} className="py-6 md:py-12 container max-w-7xl mx-auto px-6 md:px-12 my-[100px]">
                <h2 ref={offerHeadingRef} className="font-[600] text-[34px] sm:text-[40px] leading-[40px] -tracking-[1.2px] md:mb-10 mb-6 text-center text-black">What You Get</h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <div ref={(el) => (offerCardRefs.current[0] = el)} className="bg-orange-600 text-white md:p-20 p-8 rounded-lg h-96 text-left flex flex-col justify-center relative overflow-hidden"> {/* Added flex flex-col justify-center */}
                        <h3 className="text-3xl sm:text-4xl font-medium mb-6">Dedicated Workspace</h3>
                        <p className='font-medium text-lg sm:text-xl'>Full access to our Noida office (15–20 seats) for focused execution.</p>
                        <div className='absolute right-0 top-0 w-10 h-full bg-black opacity-[0.3]'>
                            <div className='relative w-full h-full flex items-center'>
                                <div className='w-10 h-10 rounded-full absolute -left-4 bg-black'></div>
                            </div>
                        </div>
                    </div>

                    <div ref={(el) => (offerCardRefs.current[1] = el)} className="overflow-hidden relative bg-[#1939a6] text-white md:p-20 p-8 rounded-lg h-96 text-left flex flex-col justify-center"> {/* Added flex flex-col */}
                        <h3 className="text-3xl sm:text-4xl font-medium mb-6">Hands-On Execution</h3>
                        <p className='font-medium text-lg sm:text-xl'>Work directly with GTM, tech, content, and growth operators.</p>
                        <div className='absolute right-0 top-0 w-10 h-full bg-black opacity-[0.3]'>
                            <div className='relative w-full h-full flex items-center'>
                                <div className='w-10 h-10 rounded-full absolute -left-4 bg-black'></div>
                            </div>
                        </div>
                    </div>

                    <div ref={(el) => (offerCardRefs.current[2] = el)} className="overflow-hidden relative bg-[#1939a6] text-white md:p-20 p-8 rounded-lg max-md:h-96 h-full text-left flex flex-col max-md:justify-center"> {/* Added flex flex-col */}
                        <h3 className="text-3xl sm:text-4xl font-medium mb-6">Weekly Deep-Dive Clinics</h3>
                        <div className='flex flex-row max-[1107px]:flex-wrap justify-between gap-3'>
                            <ol className='flex flex-col gap-y-2 list-disc pl-5'>
                                <li className='font-medium text-lg sm:text-xl'>GTM & sales</li>
                                <li className='font-medium text-lg sm:text-xl'>Hiring & ops</li>
                                <li className='font-medium text-lg sm:text-xl'>Storytelling + pitch</li>
                                <li className='font-medium text-lg sm:text-xl'>Fundraising readiness</li>
                                <li className='font-medium text-lg sm:text-xl'>Product build & UI/UX</li>
                            </ol>
                            <ol className='flex flex-col gap-y-2 list-disc pl-5'>
                                <li className='font-medium text-lg sm:text-xl'>Tech architecture</li>
                                <li className='font-medium text-lg sm:text-xl'>Founder mindset</li>
                            </ol>
                        </div>
                        <div className='absolute right-0 top-0 w-10 h-full bg-black opacity-[0.3]'>
                            <div className='relative w-full h-full flex items-center'>
                                <div className='w-10 h-10 rounded-full absolute -left-4 bg-black'></div>
                            </div>
                        </div>
                    </div>

                    <div ref={(el) => (offerCardRefs.current[3] = el)} className="overflow-hidden relative bg-orange-600 text-white md:p-20 p-8 rounded-lg max-md:h-96 h-full text-left flex flex-col max-md:justify-center"> {/* Added flex flex-col */}
                        <h3 className="text-3xl sm:text-4xl font-medium mb-6">Access to Networks</h3>
                        <ol className='flex flex-col gap-y-2 list-disc pl-5'>
                            <li className='font-medium text-lg sm:text-xl'>Angel investors</li>
                            <li className='font-medium text-lg sm:text-xl'>VC analysts & partners</li>
                            <li className='font-medium text-lg sm:text-xl'>Corporate innovation teams</li>
                            <li className='font-medium text-lg sm:text-xl'>100+ founders from the TurningIdeas ecosystem</li>
                        </ol>
                        <div className='absolute right-0 top-0 w-10 h-full bg-black opacity-[0.3]'>
                            <div className='relative w-full h-full flex items-center'>
                                <div className='w-10 h-10 rounded-full absolute -left-4 bg-black'></div>
                            </div>
                        </div>
                    </div>

                    <div ref={(el) => (offerCardRefs.current[4] = el)} className="overflow-hidden relative bg-orange-600 text-white md:p-20 p-8 rounded-lg h-96 text-left flex flex-col justify-center"> {/* Added flex flex-col */}
                        <h3 className="text-3xl sm:text-4xl font-medium mb-6">Founder Spotlights</h3>
                        <p className='font-medium text-lg sm:text-xl'>Appear on TurningTalks Podcast to boost visibility + credibility.</p>
                        <div className='absolute right-0 top-0 w-10 h-full bg-black opacity-[0.3]'>
                            <div className='relative w-full h-full flex items-center'>
                                <div className='w-10 h-10 rounded-full absolute -left-4 bg-black'></div>
                            </div>
                        </div>
                    </div>

                    <div ref={(el) => (offerCardRefs.current[5] = el)} className="overflow-hidden relative bg-[#1939a6] text-white md:p-20 p-8 rounded-lg h-96 text-left flex flex-col justify-center"> {/* Added flex flex-col */}
                        <h3 className="text-3xl sm:text-4xl font-medium mb-6">Demo Day</h3>
                        <p className='font-medium text-lg sm:text-xl'>Pitch to curated investors at the end of 12 weeks.</p>
                        <div className='absolute right-0 top-0 w-10 h-full bg-black opacity-[0.3]'>
                            <div className='relative w-full h-full flex items-center'>
                                <div className='w-10 h-10 rounded-full absolute -left-4 bg-black'></div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            {/* What you get */}

            <HowWePartner />

            <ChooseYourPath />

            <ProgramSection />

            <FAQSection />

            <section className="md:py-12 bg-black">
                <div className="container mx-auto px-6 md:px-12">
                    <MediaCarousel mediaItems={mediaData} />
                </div>
            </section>

            <CTA
                heading="Want a Partner to Co-Build"
                paragraph="Whether you're a Startup, Investor or Enterprise, We help you Scale, Innovate and Succeed."
                buttonText="Contact Us Today!"
            />

        </div>
    )
}
export default LaunchHub; 